import React, { useMemo } from "react";
import { cx } from "../../Util/classHelpers";
import {
    ChPlusCircleIcon, ChCheckIcon, ChAlertIcon, ChClockIcon, ChTrashIcon,
    ChChartIcon, ChRouteIcon, ChTimerIcon,
} from "../Icons/ch-analytics-icons";
import styles from "./chSummaryCards.module.css";

const fmt = (v) => (v == null ? "\u2014" : v.toLocaleString());
const fmtDec = (v, unit = "") => (v == null ? "\u2014" : `${v.toFixed(1)}${unit}`);
const fmtDuration = (v) => {
    if (v == null) return "\u2014";
    if (v < 60) return `${Math.round(v)} min`;
    const h = Math.floor(v / 60);
    const m = Math.round(v % 60);
    return `${h}h ${m}m`;
};

const ChSummaryCards = ({ companies, loading }) => {
    const summary = useMemo(() => {
        if (!companies || companies.length === 0) return null;

        const totals = companies.reduce(
            (acc, c) => ({
                totalCreated: acc.totalCreated + c.totalCreated,
                totalDelivered: acc.totalDelivered + c.totalDelivered,
                deliveredByDriver: acc.deliveredByDriver + c.deliveredByDriver,
                deliveredByDispatcher: acc.deliveredByDispatcher + c.deliveredByDispatcher,
                totalFailed: acc.totalFailed + c.totalFailed,
                totalIncomplete: acc.totalIncomplete + c.totalIncomplete,
                totalDeleted: acc.totalDeleted + c.totalDeleted,
                _wOntime: acc._wOntime + c.ontimePercentage * c.totalDelivered,
                _wDrive: acc._wDrive + c.avgDriveTimeMinutes * c.totalDelivered,
                _wDeliv: acc._wDeliv + c.avgDeliveryTimeMinutes * c.totalDelivered,
            }),
            {
                totalCreated: 0, totalDelivered: 0, deliveredByDriver: 0,
                deliveredByDispatcher: 0, totalFailed: 0, totalIncomplete: 0,
                totalDeleted: 0, _wOntime: 0, _wDrive: 0, _wDeliv: 0,
            }
        );

        const d = totals.totalDelivered || 1;
        return {
            ...totals,
            ontimePercentage: parseFloat((totals._wOntime / d).toFixed(1)),
            avgDriveTimeMinutes: parseFloat((totals._wDrive / d).toFixed(1)),
            avgDeliveryTimeMinutes: parseFloat((totals._wDeliv / d).toFixed(1)),
        };
    }, [companies]);

    const s = summary;

    const deliveredSub = s ? (
        <span>
            <span className={styles.subBold}>{fmt(s.deliveredByDriver)} via driver</span>
            {" \u00B7 "}
            <span className={styles.subBold}>{fmt(s.deliveredByDispatcher)} by dispatcher</span>
        </span>
    ) : null;

    return (
        <div className={styles.sectionsWrap}>
            <div>
                <div className={styles.sectionLabel}>Order Lifecycle</div>
                <div className={styles.cards}>
                    <SummaryCard variant="created" title="Created orders"
                        value={loading ? null : fmt(s?.totalCreated)} sub="orders placed this period" icon={<ChPlusCircleIcon />} />
                    <SummaryCard variant="delivered" title="Delivered orders"
                        value={loading ? null : fmt(s?.totalDelivered)} sub={deliveredSub} icon={<ChCheckIcon />} />
                    <SummaryCard variant="failed" title="Failed orders"
                        value={loading ? null : fmt(s?.totalFailed)} sub="rejected or cancelled by driver" icon={<ChAlertIcon />} />
                    <SummaryCard variant="incomplete" title="Incomplete orders"
                        value={loading ? null : fmt(s?.totalIncomplete)} sub="never assigned to a driver" icon={<ChClockIcon />} />
                    <SummaryCard variant="deleted" title="Deleted orders"
                        value={loading ? null : fmt(s?.totalDeleted)} sub="deleted this period" icon={<ChTrashIcon />} />
                </div>
            </div>

            <div>
                <div className={styles.sectionLabel}>Quality Metrics</div>
                <div className={styles.cards}>
                    <SummaryCard variant="ontime" title="% On-time deliveries"
                        value={loading ? null : fmtDec(s?.ontimePercentage, "%")} icon={<ChChartIcon />} />
                    <SummaryCard variant="avgway" title="Avg. on the way"
                        value={loading ? null : fmtDuration(s?.avgDriveTimeMinutes)} icon={<ChRouteIcon />} />
                    <SummaryCard variant="avgplace" title={"Avg. placement \u2192 delivery"}
                        value={loading ? null : fmtDuration(s?.avgDeliveryTimeMinutes)} icon={<ChTimerIcon />} />
                </div>
            </div>
        </div>
    );
};

const SummaryCard = ({ variant, title, value, sub, icon }) => {
    return (
        <div className={styles.card}>
            <div className={styles.cardTop}>
                <span className={styles.cardTitle}>{title}</span>
                <span className={cx(styles.iconBg, styles[`icon_${variant}`])}>{icon}</span>
            </div>
            <div className={cx(styles.value, styles[`val_${variant}`], value == null && styles.skel)}>
                {value ?? ""}
            </div>
            {sub && <div className={styles.sub}>{sub}</div>}
        </div>
    );
};

export default ChSummaryCards;
